import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Play, Pause, Heart, Check, ChevronDown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { inputCls } from "@/components/admin/kit";

const uniq = (list, k) => [...new Set(list.map((v) => v[k]).filter(Boolean))].sort();

const Filter = ({ label, value, onChange, options, testid }) => (
  <select className={`${inputCls} w-auto rounded-full`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="">{label}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

function usePreview(lang) {
  const api = useApi();
  const audio = useRef(new Audio());
  const [state, setState] = useState({ id: null, loading: false });
  const stop = () => { audio.current.pause(); setState({ id: null, loading: false }); };
  const toggle = async (id) => {
    const a = audio.current;
    if (state.id === id && !state.loading) { stop(); return; }
    a.pause();
    setState({ id, loading: true });
    try {
      const r = await api.post("/voices/preview", { voice: id, language: lang.code, pace: lang.pace, loudness: lang.loudness, pitch: lang.pitch, sample_rate: lang.sample_rate }, { responseType: "blob" });
      if (a.src.startsWith("blob:")) URL.revokeObjectURL(a.src);
      a.src = URL.createObjectURL(r.data);
      a.onended = () => setState({ id: null, loading: false });
      await a.play();
      setState({ id, loading: false });
    } catch (e) { toast.error(errMsg(e)); setState({ id: null, loading: false }); }
  };
  return { state, toggle, stop };
}

function VoiceRow({ v, selected, fav, onPick, onFav, preview }) {
  const playing = preview.state.id === v.id;
  return (
    <li className={`flex items-center gap-3 border-b border-[var(--line,#eee)] px-2 py-3 ${selected ? "bg-[var(--lav)]" : ""}`} data-testid={`voice-row-${v.id}`}>
      <button onClick={() => preview.toggle(v.id)} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[var(--violet)] text-white" aria-label={`Play ${v.name}`} data-testid={`voice-play-${v.id}`}>
        {playing && preview.state.loading ? <Loader2 size={18} className="animate-spin" /> : playing ? <Pause size={18} /> : <Play size={18} />}
      </button>
      <button className="min-w-0 flex-1 text-left" onClick={() => onPick(v.id)} data-testid={`voice-pick-${v.id}`}>
        <p className="font-semibold">{v.name} <span className="text-xs font-normal text-muted-foreground">· {v.language} · {v.gender}</span></p>
        <p className="truncate text-sm text-muted-foreground">{v.description}</p>
      </button>
      <span className="hidden rounded-full bg-gray-100 px-2 py-0.5 text-xs sm:inline">{v.use_case}</span>
      <span className="w-5">{selected && <Check size={18} className="text-[var(--violet)]" data-testid={`voice-selected-${v.id}`} />}</span>
      <button onClick={() => onFav(v.id)} aria-label="Favourite" data-testid={`voice-fav-${v.id}`}><Heart size={18} className={fav ? "fill-[var(--orange)] text-[var(--orange)]" : "text-muted-foreground"} /></button>
    </li>
  );
}

export function VoiceLibrary({ lang, setLang }) {
  const [all] = useLoad("/voices");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ language: "", gender: "", use_case: "" });
  const [tab, setTab] = useState("all");
  const preview = usePreview(lang);
  const favs = useMemo(() => lang.voice_options || [], [lang.voice_options]);
  const list = useMemo(() => (all || []).filter((v) =>
    (tab === "all" || favs.includes(v.id)) && (!f.language || v.language === f.language) && (!f.gender || v.gender === f.gender)
    && (!f.use_case || v.use_case === f.use_case)
    && (!q || `${v.name} ${v.description} ${v.use_case} ${v.id}`.toLowerCase().includes(q.toLowerCase()))), [all, q, f, tab, favs]);
  const cur = (all || []).find((v) => v.id === lang.voice);
  const fav = (id) => setLang({ ...lang, voice_options: favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id] });
  const pick = (id) => { setLang({ ...lang, voice: id, voice_options: favs.includes(id) ? favs : [...favs, id] }); preview.stop(); setOpen(false); };
  return (
    <>
      <button type="button" className={`${inputCls} flex items-center gap-2 text-left`} onClick={() => setOpen(true)} data-testid="lang-voice-open">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--lav)] text-[10px] font-bold">{(cur?.name || lang.voice || "?").slice(0, 2).toUpperCase()}</span>
        <span className="flex-1 truncate" data-testid="lang-voice-current">{cur ? `${cur.name} · ${cur.use_case}` : lang.voice}</span>
        <ChevronDown size={16} />
      </button>
      <Dialog open={open} onOpenChange={(o) => { if (!o) preview.stop(); setOpen(o); }}>
        <DialogContent className="max-w-3xl" data-testid="voice-library">
          <DialogHeader><DialogTitle>Voices</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2">
            <label className={`${inputCls} flex min-w-[200px] flex-1 items-center gap-2 rounded-full`}><Search size={16} /><input className="flex-1 bg-transparent outline-none" placeholder="Search voices" value={q} onChange={(e) => setQ(e.target.value)} data-testid="voice-search" /></label>
            <Filter label="Languages" value={f.language} onChange={(v) => setF({ ...f, language: v })} options={uniq(all || [], "language")} testid="voice-filter-language" />
            <Filter label="Genders" value={f.gender} onChange={(v) => setF({ ...f, gender: v })} options={uniq(all || [], "gender")} testid="voice-filter-gender" />
            <Filter label="Use Cases" value={f.use_case} onChange={(v) => setF({ ...f, use_case: v })} options={uniq(all || [], "use_case")} testid="voice-filter-usecase" />
          </div>
          <div className="flex gap-6 border-b text-sm font-semibold">
            {[["all", "All voices"], ["fav", "Favorites"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`whitespace-nowrap pb-2 ${tab === k ? "border-b-2 border-[var(--ink)]" : "text-muted-foreground"}`} data-testid={`voice-tab-${k}`}>{l}</button>
            ))}
            <span className="ml-auto pb-2 text-xs font-normal text-muted-foreground" data-testid="voice-count">{list.length} voices · ▶ plays a short Telugu line with your settings</span>
          </div>
          <ul className="max-h-[55vh] overflow-y-auto" data-testid="voice-list">
            {list.map((v) => <VoiceRow key={v.id} v={v} selected={v.id === lang.voice} fav={favs.includes(v.id)} onPick={pick} onFav={fav} preview={preview} />)}
          </ul>
          <p className="text-xs text-muted-foreground">Pick a voice, then press Save on the page. Every voice can speak Telugu.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

const SliderRow = ({ label, value, min, max, step, onChange, testid, fmt = (v) => v }) => (
  <div className="grid gap-1">
    <p className="text-sm font-semibold">{label}</p>
    <div className="flex items-center gap-3">
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[var(--violet)]" data-testid={`${testid}-slider`} />
      <input type="number" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className={`${inputCls} !w-24 shrink-0 text-center`} data-testid={`${testid}-input`} />
      <span className="w-10 text-xs text-muted-foreground">{fmt(value)}</span>
    </div>
  </div>
);

export function VoiceSettings({ lang, setLang }) {
  const set = (k) => (v) => setLang({ ...lang, [k]: v });
  return (
    <div className="grid gap-4 rounded-2xl bg-[var(--lav)]/50 p-4" data-testid="voice-settings">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><p className="mb-1 text-sm font-semibold">Model</p><p className={`${inputCls} bg-white`} data-testid="voice-model">Bulbul v4 Flash</p></div>
        <div><p className="mb-1 text-sm font-semibold">Voice</p><VoiceLibrary lang={lang} setLang={setLang} /></div>
        <div><p className="mb-1 text-sm font-semibold">Target language</p><p className={`${inputCls} bg-white`} data-testid="voice-target-language">{lang.name}</p></div>
      </div>
      <SliderRow label="Pace" value={lang.pace ?? 1} min={0.5} max={2} step={0.05} onChange={set("pace")} testid="lang-pace" fmt={(v) => `${v}x`} />
      <SliderRow label="Loudness" value={lang.loudness ?? 1} min={0.3} max={3} step={0.1} onChange={set("loudness")} testid="lang-loudness" fmt={(v) => `${v}x`} />
      <SliderRow label="Pitch" value={lang.pitch ?? 0} min={-0.75} max={0.75} step={0.05} onChange={set("pitch")} testid="lang-pitch" />
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="grid gap-1 text-sm font-semibold">Sample rate (Hz)
          <select className={inputCls} value={lang.sample_rate ?? 24000} onChange={(e) => set("sample_rate")(Number(e.target.value))} data-testid="lang-sample-rate">
            {[8000, 16000, 22050, 24000, 32000, 44100, 48000].map((r) => <option key={r} value={r}>{r >= 1000 ? `${r / 1000}k` : r}</option>)}
          </select>
        </label>
        <div><p className="mb-1 text-sm font-semibold">Audio codec</p><p className={`${inputCls} bg-white`} data-testid="voice-codec">MP3 · 128k (as Sarvam)</p></div>
        <div><p className="mb-1 text-sm font-semibold">Phone version</p><p className={`${inputCls} bg-white`}>8k WAV (always, for calls)</p></div>
      </div>
      <p className="text-xs text-muted-foreground">Same settings and the same Sarvam request as the playground (REST Stream, MP3 128k). Slow-pace patients get pace ×0.85. Phone calls also get an 8k WAV copy because phone systems need it.</p>
    </div>
  );
}
